import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  HiShoppingCart,
  HiCube,
  HiClipboardList,
  HiDotsHorizontal,
  HiHome,
  HiDocumentText,
  HiCurrencyDollar,
  HiCog,
  HiShieldCheck,
  HiLogout,
  HiX,
} from 'react-icons/hi';

const PRIMARY_TABS = [
  { path: '/admin/orders/retail', label: 'Retail', icon: HiShoppingCart, match: (pathname) => pathname === '/admin/orders/retail' },
  { path: '/admin/orders/wholesale', label: 'Wholesale', icon: HiCube, match: (pathname) => pathname === '/admin/orders/wholesale' },
  { path: '/admin/orders', label: 'Orders', icon: HiClipboardList, match: (pathname) => pathname === '/admin/orders' },
];

const MORE_ITEMS = [
  { path: '/admin/overview', label: 'Overview', icon: HiHome, description: 'Dashboard & stats', match: (pathname) => pathname === '/admin/overview' },
  { path: '/admin/inventory/gallery', label: 'Inventory Gallery', icon: HiDocumentText, description: 'Visual inventory', match: (pathname) => pathname.startsWith('/admin/inventory/gallery') },
  { path: '/admin/finance', label: 'Finance', icon: HiCurrencyDollar, description: 'Settlement reports', match: (pathname) => pathname.startsWith('/admin/finance') },
  { path: '/admin/settings', label: 'Settings', icon: HiCog, description: 'Order numbering & go-live', match: (pathname) => pathname.startsWith('/admin/settings') },
  { path: '/admin/login-history', label: 'Login History', icon: HiShieldCheck, description: 'Authentication logs', gated: true, match: (pathname) => pathname.startsWith('/admin/login-history') },
];

function isMoreRouteActive(pathname) {
  return MORE_ITEMS.some((item) => item.match(pathname));
}

const AdminMobileNav = ({ pendingInquiryCount = 0, hasLoginHistoryAccess = false, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  const closeMore = () => setMoreOpen(false);

  const handleMoreNavigate = (path) => {
    closeMore();
    navigate(path);
  };

  const moreActive = isMoreRouteActive(location.pathname);

  return (
    <>
      <nav
        className="admin-mobile-nav lg:hidden"
        aria-label="Admin mobile navigation"
      >
        {PRIMARY_TABS.map(({ path, label, icon: Icon, match }) => {
          const isActive = match(location.pathname);
          return (
            <Link
              key={path}
              to={path}
              className={`admin-mobile-nav-tab ${isActive ? 'admin-mobile-nav-tab--active' : ''}`}
            >
              <span className="relative inline-flex">
                <Icon className="w-5 h-5" aria-hidden="true" />
                {path === '/admin/orders' && pendingInquiryCount > 0 && (
                  <span className="admin-mobile-nav-badge admin-mobile-nav-badge--pulse">
                    {pendingInquiryCount}
                  </span>
                )}
              </span>
              <span>{label}</span>
            </Link>
          );
        })}

        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          className={`admin-mobile-nav-tab ${moreActive ? 'admin-mobile-nav-tab--active' : ''}`}
          aria-expanded={moreOpen}
          aria-haspopup="dialog"
        >
          <HiDotsHorizontal className="w-5 h-5" aria-hidden="true" />
          <span>More</span>
        </button>
      </nav>

      {moreOpen && (
        <>
          <div
            className="admin-mobile-sheet-backdrop lg:hidden"
            onClick={closeMore}
            aria-hidden="true"
          />
          <div
            className="admin-mobile-more-sheet lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="More admin pages"
          >
            <div className="admin-mobile-more-sheet-header">
              <h2 className="text-base font-semibold text-gray-900">More</h2>
              <button
                type="button"
                onClick={closeMore}
                className="min-h-11 min-w-11 flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
                aria-label="Close menu"
              >
                <HiX className="w-5 h-5" />
              </button>
            </div>

            <div className="admin-mobile-more-sheet-body">
              {MORE_ITEMS.filter((item) => !item.gated || hasLoginHistoryAccess).map((item) => {
                const Icon = item.icon;
                const isActive = item.match(location.pathname);

                return (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => handleMoreNavigate(item.path)}
                    className={`admin-mobile-more-item ${isActive ? 'admin-mobile-more-item--active' : ''}`}
                  >
                    <Icon className="w-5 h-5 shrink-0" aria-hidden="true" />
                    <div className="min-w-0 text-left">
                      <div className="font-medium text-sm">{item.label}</div>
                      <div className="text-xs text-gray-500 truncate">{item.description}</div>
                    </div>
                  </button>
                );
              })}

              <button
                type="button"
                onClick={() => {
                  closeMore();
                  onLogout?.();
                }}
                className="admin-mobile-more-item admin-mobile-more-item--danger mt-2"
              >
                <HiLogout className="w-5 h-5 shrink-0" aria-hidden="true" />
                <div className="text-left">
                  <div className="font-medium text-sm">Sign Out</div>
                </div>
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AdminMobileNav;
